import React from "react";
import { CreditCard, AlertTriangle } from "lucide-react";

/**
 * Modal inline para decisión de aprobación CFO (aprobar/rechazar).
 *
 * Iter 129 · categorías de rechazo estructuradas + free-text obligatorio.
 * Props:
 *   open, form ({action, comments, reject_category?}), onFormChange, onCancel, onConfirm, loading
 */
const REJECT_CATEGORIES = [
  { key: "over_budget",     label: "Fuera de presupuesto",     desc: "El CAPEX o OPEX supera el umbral aprobable" },
  { key: "wrong_sector",    label: "Sector no estratégico",    desc: "Actividad fuera del core Valoriza" },
  { key: "no_capex",        label: "Falta CAPEX / definición", desc: "Presupuesto incompleto o sin capex definido" },
  { key: "low_margin",      label: "Margen insuficiente",      desc: "EBITDA / IRR bajo el mínimo" },
  { key: "risk_juridico",   label: "Riesgo jurídico / pliego", desc: "Cláusulas contractuales problemáticas" },
  { key: "other",           label: "Otro (ver comentarios)",   desc: "Motivo no listado, detallar abajo" },
];

export function CfoApproveModal({ open, form, onFormChange, onCancel, onConfirm, loading }) {
  if (!open) return null;
  const isReject = form.action === "reject";
  const isConditional = form.action === "approve_with_conditions";

  return (
    <div className="mt-4 p-4 border border-blue-200 rounded-xl space-y-3 bg-blue-50/50" data-testid="cfo-approve-modal">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={15} className="text-blue-600" />
        <p className="text-sm font-semibold text-gray-900">Decisión Aprobación CFO</p>
      </div>
      <div className="flex gap-2">
        {[
          ["approve", "Aprobar", "bg-green-500 border-green-500 text-white"],
          ["approve_with_conditions", "Aprobar con condiciones", "bg-amber-500 border-amber-500 text-white"],
          ["reject",  "Rechazar",       "bg-red-500 border-red-500 text-white"],
        ].map(([val, label, cls]) => (
          <button
            key={val}
            onClick={() => onFormChange({ ...form, action: val })}
            data-testid={`cfo-action-${val}`}
            className={`flex-1 py-2.5 text-xs font-semibold rounded-lg border-2 transition-all ${
              form.action === val ? cls : "border-gray-200 text-gray-600 bg-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isConditional && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="cfo-conditions-box">
          <label className="text-xs font-bold text-amber-800 mb-1 block">Condiciones exigidas (obligatorio)</label>
          <textarea
            rows={2}
            value={form.conditions || ""}
            onChange={e => onFormChange({ ...form, conditions: e.target.value })}
            placeholder="Ej: supeditado a firma de UTE antes del 15/11..."
            data-testid="cfo-conditions-input"
            className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 resize-none"
          />
        </div>
      )}

      {/* iter 129 · categorías estructuradas de rechazo */}
      {isReject && (
        <div className="bg-white border border-red-200 rounded-lg p-3" data-testid="cfo-reject-reasons">
          <div className="flex items-center gap-1.5 text-xs font-bold text-red-800 mb-2">
            <AlertTriangle size={12}/> Motivo del rechazo (obligatorio)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {REJECT_CATEGORIES.map(cat => (
              <label
                key={cat.key}
                className={`text-[11px] cursor-pointer border rounded-lg px-2 py-1.5 transition ${
                  form.reject_category === cat.key
                    ? "bg-red-100 border-red-500 text-red-900"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}>
                <input
                  type="radio"
                  name="cfo-reject-category"
                  checked={form.reject_category === cat.key}
                  onChange={() => onFormChange({ ...form, reject_category: cat.key })}
                  data-testid={`cfo-reject-cat-${cat.key}`}
                  className="mr-1.5 accent-red-600"/>
                <b>{cat.label}</b>
                <div className="text-[10px] opacity-70 ml-4">{cat.desc}</div>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1 block">
          {isReject ? "Comentarios (obligatorio)" : "Comentarios (opcional)"}
        </label>
        <textarea
          rows={3}
          value={form.comments}
          onChange={e => onFormChange({ ...form, comments: e.target.value })}
          placeholder={isReject ? "Detalla el motivo específico del rechazo..." : "Observaciones o condiciones de aprobación..."}
          data-testid="cfo-comments-input"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm}
          disabled={loading || (isReject && (!form.comments || !form.reject_category)) || (isConditional && !(form.conditions || "").trim())}
          className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          data-testid="confirm-cfo-approve-btn"
        >
          {loading ? "Procesando…" : isReject ? "Confirmar rechazo" : "Confirmar aprobación"}
        </button>
      </div>
    </div>
  );
}
